<script setup lang="ts">
//#region import
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import numeral from 'numeral';
import { useSessionAction } from '@/composables/useSessionAction';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useFilters } from '@/setup/filters';

import type {
  GetControlTowerCustomersAffiliatePaymentsSettingsResponse,
  GetControlTowerCustomersAffiliateProgramNameResponse,
  GetControlTowerCustomersAffiliateReferralProgramSettingsResponse,
  GetControlTowerCustomersAffiliateTelegramBotSettingsResponse,
  GetControlTowerCustomersDefaultDomainsNotificationsDefaultSettingsResponse,
  GetControlTowerCustomersEmailSettingsResponse,
  GetControlTowerCustomersKycSettingsResponse,
  GetControlTowerCustomersOfferDistributionSettingsResponse,
  GetControlTowerCustomersGeoTrafficSettingsResponse,
  GetControlTowerCustomersPrivacyPolicySettingsResponse,
  GetControlTowerCustomersSupportContactsSettingsResponse,
  GetControlTowerCustomersIntercomSettingsResponse,
  PutControlTowerCustomersEmailSettingsData,
  PutControlTowerCustomersKycSettingsData,
  GetControlTowerCustomersAffiliatesLimitsResponse,
  PutControlTowerCustomersAffiliateProgramNameData,
  PutControlTowerCustomersAffiliateTelegramBotSettingsData,
  PutControlTowerCustomersPrivacyPolicySettingsData,
  PutControlTowerCustomersSupportContactsSettingsData,
  PutControlTowerCustomersAffiliateReferralProgramSettingsData,
  PutControlTowerCustomersAffiliatePaymentsSettingsData,
  PutControlTowerCustomersDefaultDomainsNotificationsSettingsData,
  PutControlTowerCustomersAffiliatesLimitsData,
  PutControlTowerCustomersOfferDistributionSettingsData,
  PutControlTowerCustomersGeoTrafficSettingsData,
  GetControlTowerCustomersDomainReachabilitySettingsResponse,
  PutControlTowerCustomersDomainReachabilitySettingsData,
  GetAffiliateOffersCatalogResponses,
} from '@/types/openapi-ts';
import type { NonNullableField } from '@/types/utils';
//#endregion

//#region types
export type SystemSettingsData = Awaited<ReturnType<typeof fetchData>>;
type Offer = GetAffiliateOffersCatalogResponses[200]['payload'][number];

interface GroupedCategory {
  category_id: string;
  category_name: string;
  offers: Offer[];
}

export interface PopupField {
  title: string,
  key: keyof SystemSettingsData,
  type: string,
  hint?: string,
  hintIsHtml?: boolean,
  renderPost?: () => string;
  label?: (enabled: boolean) => string;
  placeholder?: string;
  options?: (...params: any[]) => any;
  disabled?: (...params: any[]) => boolean;
  compact?: boolean;
  errorPathInput?: string[];
  errorPathSelect?: string[];
  selectKey?: string;
  groupKey?: string;
  dataKeys?: string[];
  path?: string[];
  normalizeValue?: (value: string) => string;
}

interface PopupConfig {
  title: string;
  info?: string;
  fields: PopupField[];
  action: (...params: any[]) => Promise<any>;
}

interface FieldHeaderConfig {
  title: string;
  key: string;
  hint?: string;
}

interface FieldRowsConfig<T> {
  title?: string;
  hint?: string;
  hintIsHtml?: boolean;
  key?: keyof SystemSettingsData;
  defaultText?: string;
  messenger?: string;
  preIcon?: string;
  className?: string;
  renderValue?: (data: T) => string;
  renderStatus?: (data: T) => {
    text: string;
    color: string;
    hint: string | null;
  };
  show?: (data: T) => boolean;
}

interface FieldsConfig<T> {
  header: FieldHeaderConfig;
  rows: FieldRowsConfig<T>[];
}

interface IntegrationStatuses {
  status: 'disabled' | 'on' | 'error' | 'unknown';
  last_checked_at: string | null;
  error_message: string | null;
}

type KycRules = NonNullableField<PutControlTowerCustomersKycSettingsData['body']['kyc_rules']>;

type IntegrationStatusesPayload = {
  intercom_status: IntegrationStatuses;
  sumsub_status: IntegrationStatuses;
};
//#endregion

//#region const


const popup = {
  affiliateProgramName: {
    title: 'settings.systemSettings.popup.affiliateProgram',
    fields: [
      {
        title: 'settings.systemSettings.affiliateProgramName',
        key: 'affiliate_program_name',
        type: 'text',
      },
    ],
    action: (params: PutControlTowerCustomersAffiliateProgramNameData['body']) => api.putCustomersAffiliateProgramName(params),
  },
  emailSettings: {
    title: 'settings.systemSettings.emailSettings',
    fields: [
      {
        title: 'settings.systemSettings.transactionalEmailSender',
        key: 'transactional_email_from_mailbox',
        type: 'text',
        renderPost: () => `@${data.value.transactional_email_from_domain}`,
        hint: 'settings.systemSettings.serviceEmailHint',
      },
      {
        title: 'settings.systemSettings.replyTo',
        key: 'transactional_email_reply_to_address',
        type: 'text',
        hint: 'settings.systemSettings.supportEmailHint',
      },
    ],
    action: ({ transactional_email_reply_to_address, transactional_email_from_mailbox }: PutControlTowerCustomersEmailSettingsData['body']) =>
      api.putCustomersEmailSettings({
          transactional_email_reply_to_address,
          transactional_email_from_mailbox,
        },
      ),
  },
  affiliatesTelegramBot: {
    title: 'settings.systemSettings.affiliatesTelegramBot',
    info: 'settings.systemSettings.affiliatesTelegramBotHint',
    fields: [
      {
        title: 'settings.systemSettings.botUsername',
        key: 'affiliates_telegram_bot_username',
        type: 'text',
      },
      {
        title: 'settings.systemSettings.botToken',
        key: 'affiliates_telegram_bot_token',
        type: 'text',
      },
    ],
    action: ({ affiliates_telegram_bot_token, affiliates_telegram_bot_username }: PutControlTowerCustomersAffiliateTelegramBotSettingsData['body']) =>
      api.putCustomersAffiliateTelegramBotSettings({
        affiliates_telegram_bot_token: affiliates_telegram_bot_token || null,
        affiliates_telegram_bot_username: affiliates_telegram_bot_username || null,
      }),
  },
  policies: {
    title: 'settings.systemSettings.policies',
    info: 'settings.systemSettings.policiesHint',
    fields: [
      {
        title: 'settings.systemSettings.termsOfServiceURL',
        key: 'tos_url',
        type: 'text',
      },
      {
        title: 'settings.systemSettings.privacyPolicyURL',
        key: 'privacy_policy_url',
        type: 'text',
      },
    ],
    action: (params: PutControlTowerCustomersPrivacyPolicySettingsData['body']) => api.putCustomersPrivacyPolicySettings(params),
  },
  supportContacts: {
    title: 'settings.systemSettings.supportContacts',
    info: 'settings.systemSettings.supportContactsHint',
    fields: [
      {
        title: 'settings.systemSettings.showContactsInAffiliatePanel',
        key: 'panel_contacts_enabled',
        label: (isEnabled: boolean) => (isEnabled ? 'settings.systemSettings.enabled' : 'settings.systemSettings.disabled'),
        type: 'boolean',
      },
      {
        title: 'settings.systemSettings.ms_teams',
        key: 'panel_contact_ms_teams',
        type: 'text',
      },
      {
        title: 'settings.systemSettings.telegram',
        key: 'panel_contact_telegram',
        type: 'text',
      },
      {
        title: 'settings.systemSettings.email',
        key: 'panel_contact_email',
        type: 'text',
      },
      {
        title: 'settings.systemSettings.contact_us_form',
        key: 'panel_contact_us_form',
        type: 'text',
      },
    ],
    action: (params: PutControlTowerCustomersSupportContactsSettingsData['body']) => api.putCustomersSupportContactsSettings(params),
  },
  intercomSetup: {
    title: 'settings.systemSettings.intercomSetup',
    fields: [
      {
        title: 'settings.systemSettings.intercomEnabled',
        key: 'intercom_enabled',
        label: (isEnabled: boolean) => (isEnabled ? 'settings.systemSettings.enabled' : 'settings.systemSettings.disabled'),
        type: 'boolean',
      },
      {
        title: 'settings.systemSettings.appId',
        key: 'app_id',
        type: 'password-text',
        disabled: ({ intercom_enabled }: SystemSettingsData) => !intercom_enabled,
      },
      {
        title: 'settings.systemSettings.appKey',
        key: 'app_key',
        type: 'password-text',
        disabled: ({ intercom_enabled }: SystemSettingsData) => !intercom_enabled,
      },
      {
        title: 'settings.systemSettings.appSecretKey',
        key: 'app_secret_key',
        type: 'password-text',
        disabled: ({ intercom_enabled }: SystemSettingsData) => !intercom_enabled,
      },
    ],
    action: ({ intercom_enabled, app_id, app_key, app_secret_key }: SystemSettingsData) => {
      if (intercom_enabled) {
        return api.putCustomersIntercomSettings({
          app_id: app_id || '',
          app_key: app_key || '',
          app_secret_key: app_secret_key || '',
        });
      }

      return api.deleteCustomersIntercomSettings();
    },
  },
  kycSetup: {
    title: 'settings.systemSettings.kycSetup',
    fields: [
      {
        title: 'settings.systemSettings.kycVerification',
        key: 'kyc_enabled',
        label: (isEnabled: boolean) => (isEnabled ? 'settings.systemSettings.enabled' : 'settings.systemSettings.disabled'),
        type: 'boolean',
      },
      {
        title: 'settings.systemSettings.sumsubLevelName',
        key: 'kyc_level_name',
        type: 'text',
        hint: 'settings.systemSettings.sumsubLevelNameHint',
        hintIsHtml: true,
        disabled: ({ kyc_enabled }: SystemSettingsData) => !kyc_enabled,
      },
      {
        title: 'settings.systemSettings.sumsubAppToken',
        key: 'kyc_app_token',
        type: 'password-text',
        disabled: ({ kyc_enabled }: SystemSettingsData) => !kyc_enabled,
      },
      {
        title: 'settings.systemSettings.sumsubSecretKey',
        key: 'kyc_secret_key',
        type: 'password-text',
        disabled: ({ kyc_enabled }: SystemSettingsData) => !kyc_enabled,
      },
      {
        title: 'settings.systemSettings.sumsubSecretWebhookKey',
        key: 'kyc_secret_webhook_key',
        type: 'password-text',
        hint: 'settings.systemSettings.sumsubWebhookHint',
        hintIsHtml: true,
        disabled: ({ kyc_enabled }: SystemSettingsData) => !kyc_enabled,
      },
      {
        title: 'settings.systemSettings.kycAfterSignUp',
        key: 'kyc_on_signup_mode',
        type: 'radio',
        compact: true,
        options: () => kycAfterSignUpOptions.value,
        disabled: ({ kyc_enabled }: SystemSettingsData) => !kyc_enabled,
      },
      {
        title: '',
        key: 'kyc_on_signup_country_list',
        type: 'select-multiple',

        options: () => countries.value,
        disabled: ({ kyc_enabled, kyc_on_signup_mode }: SystemSettingsData) =>
          !kyc_enabled || kyc_on_signup_mode !== 'selected_countries',
      },
      {
        title: 'settings.systemSettings.kycDependingOnPayments',
        key: 'kyc_on_first_payment_enabled',
        type: 'radio',
        options: () => kycDependingOnPaymentsOptions.value,
        disabled: ({ kyc_enabled }: SystemSettingsData) => !kyc_enabled,
      },
      {
        title: 'settings.systemSettings.kycDependingOnLimits',
        key: 'kyc_on_limits_enabled',
        type: 'radio',
        compact: true,
        options: () => kycDependingOnLimitsOptions.value,
        disabled: ({ kyc_enabled }: SystemSettingsData) => !kyc_enabled,
      },
      {
        title: '',
        key: 'kyc_on_limits_amount',
        type: 'input-select',
        errorPathInput: ['kyc_rules', 'on_limits', 'amount'],
        errorPathSelect: ['kyc_rules', 'on_limits', 'currency_code'],
        selectKey: 'kyc_on_limits_currency_code',
        dataKeys: ['kyc_on_limits_currency_code'],
        placeholder: 'settings.currencies.currency',
        options: () => customerCurrencies.value,
        disabled: ({ kyc_enabled, kyc_on_limits_enabled }: SystemSettingsData) =>
          !kyc_enabled || !kyc_on_limits_enabled,
      },
      {
        title: 'settings.systemSettings.kycOnDemand',
        key: 'kyc_on_demand_enabled',
        hint: 'settings.systemSettings.kycOnDemandHint',
        type: 'radio',
        options: () => kycOnDemandOptions.value,
        disabled: ({ kyc_enabled }: SystemSettingsData) => !kyc_enabled,
      },
    ],
    action: (params: SystemSettingsData) => {
      const {
        kyc_enabled,
        kyc_level_name,
        kyc_app_token,
        kyc_secret_key,
        kyc_secret_webhook_key,
        kyc_on_signup_mode,
        kyc_on_signup_country_list = [],
        kyc_on_first_payment_enabled,
        kyc_on_limits_enabled,
        kyc_on_limits_amount,
        kyc_on_limits_currency_code,
        kyc_on_demand_enabled,
      } = params;

      const onSignupEnabled = kyc_on_signup_mode !== 'disabled';
      const onSignupCountryList = kyc_on_signup_mode === 'selected_countries'
        ? (kyc_on_signup_country_list || [])
        : [];

      const kycRules = {
        on_signup: {
          enabled: onSignupEnabled,
          country_list: onSignupCountryList,
        },
        on_demand: {
          enabled: !!kyc_on_demand_enabled,
        },
        on_first_payment: {
          enabled: !!kyc_on_first_payment_enabled,
        },
        on_limits: {
          enabled: !!kyc_on_limits_enabled,
          ...(kyc_on_limits_enabled && {
            amount: +(kyc_on_limits_amount ?? 0),
            currency_code: kyc_on_limits_currency_code,
          }),
        },
      };

      return api.putCustomersKycSettings({
        kyc_enabled: !!kyc_enabled,
        ...(kyc_enabled && {
          kyc_level_name: String(kyc_level_name),
          kyc_app_token: String(kyc_app_token),
          kyc_secret_key: String(kyc_secret_key),
          kyc_secret_webhook_key: String(kyc_secret_webhook_key),
        }),
        kyc_rules: kycRules,
      });
    },
  },
  defaultOffer: {
    title: 'settings.systemSettings.default_offer',
    fields: [
      {
        title: 'settings.systemSettings.popup.add_default_offer',
        key: 'offers_default_enabled',
        label: (isEnabled: boolean) => (isEnabled ? 'settings.systemSettings.enabled' : 'settings.systemSettings.disabled'),
        type: 'boolean',
      },
      {
        title: 'settings.systemSettings.popup.default_offer',
        key: 'default_cataloged_offer_id',
        type: 'select-grouped',
        placeholder: 'settings.systemSettings.popup.select_default_offer',
        groupKey: 'offers',
        disabled: ({ offers_default_enabled }: SystemSettingsData) => !offers_default_enabled,
        options: () => offersCatalogGrouped.value,
      },

      {
        title: 'settings.systemSettings.popup.add_domain_distribution',
        key: 'domain_distribution_enabled',
        label: (isEnabled: boolean) => (isEnabled ? 'settings.systemSettings.enabled' : 'settings.systemSettings.disabled'),
        type: 'boolean',
      },
      {
        title: 'settings.systemSettings.popup.domain_offer',
        key: 'cataloged_offer_id',
        path: ['distribute_by_email_domain'],
        type: 'select-grouped',
        placeholder: 'settings.systemSettings.popup.select_domain_offer',
        groupKey: 'offers',
        disabled: ({ domain_distribution_enabled }: SystemSettingsData) => !domain_distribution_enabled,
        options: () => offersCatalogGrouped.value,
      },
      {
        title: 'settings.systemSettings.popup.domains_list',
        path: ['distribute_by_email_domain'],
        key: 'email_domains',
        type: 'textarea',
        disabled: ({ domain_distribution_enabled }: SystemSettingsData) => !domain_distribution_enabled,
      },
    ],
    action: ({
               offers_default_enabled,
               default_cataloged_offer_id,
               domain_distribution_enabled,
               cataloged_offer_id,
               email_domains,
             }: SystemSettingsData & PutControlTowerCustomersOfferDistributionSettingsData['body']) => {
      const data = {
        default_cataloged_offer_id: offers_default_enabled ? default_cataloged_offer_id : null,
        distribute_by_email_domain: domain_distribution_enabled ? {
          cataloged_offer_id: cataloged_offer_id || '',
          email_domains: email_domains || '',
        } : null,
      };

      return api.putCustomersOfferDistributionSettings(data);
    },
  },
  geoblockSettings: {
    title: 'settings.systemSettings.geoblockSettings',
    fields: [
      {
        title: 'settings.systemSettings.geoblock',
        key: 'geo_traffic_enabled',
        label: (isEnabled: boolean) => (isEnabled ? 'settings.systemSettings.enabled' : 'settings.systemSettings.disabled'),
        type: 'boolean',
      },
      {
        title: '',
        key: 'geo_traffic_mode',
        type: 'radio',
        compact: true,
        options: () => [{ value: 'allow_except', label: t('settings.systemSettings.allowAllExcept') }],
        disabled: ({ geo_traffic_enabled }: SystemSettingsData) => !geo_traffic_enabled,
      },
      {
        title: '',
        key: 'geo_traffic_allow_countries',
        type: 'select-multiple',
        placeholder: 'settings.systemSettings.countryPlaceholder',
        options: () => countries.value,
        disabled: ({ geo_traffic_enabled, geo_traffic_mode }: SystemSettingsData) => !geo_traffic_enabled || geo_traffic_mode !== 'allow_except',
      },
      {
        title: '',
        key: 'geo_traffic_mode',
        type: 'radio',
        compact: true,
        options: () => [{ value: 'decline_except', label: t('settings.systemSettings.blockAllExcept') }],
        disabled: ({ geo_traffic_enabled }: SystemSettingsData) => !geo_traffic_enabled,
      },
      {
        title: '',
        key: 'geo_traffic_block_countries',
        type: 'select-multiple',
        placeholder: 'settings.systemSettings.countryPlaceholder',
        options: () => countries.value,
        disabled: ({ geo_traffic_enabled, geo_traffic_mode }: SystemSettingsData) => !geo_traffic_enabled || geo_traffic_mode !== 'decline_except',
      },
    ],
    action: ({
      geo_traffic_enabled,
      geo_traffic_mode,
      geo_traffic_allow_countries,
      geo_traffic_block_countries,
    }: SystemSettingsData) => {
      const geoTrafficCountries = geo_traffic_mode === 'decline_except'
        ? (geo_traffic_block_countries || [])
        : (geo_traffic_allow_countries || []);

      return api.putCustomersGeoTrafficSettings({
        geo_traffic_mode: geo_traffic_mode as PutControlTowerCustomersGeoTrafficSettingsData['body']['geo_traffic_mode'],
        geo_traffic_countries: geo_traffic_enabled ? geoTrafficCountries : [],
      });
    },
  },
  referralProgramDefaultSettings: {
    title: 'settings.systemSettings.referralProgramDefaultSettings',
    fields: [
      {
        title: 'settings.systemSettings.popup.referralProgram',
        key: 'affiliates_referral_enabled',
        label: (isEnabled: boolean) => (isEnabled ? 'settings.systemSettings.enabled' : 'settings.systemSettings.disabled'),
        type: 'boolean',
      },
      {
        title: 'settings.systemSettings.referralPercent',
        key: 'affiliates_referral_percent',
        type: 'number',
        renderPost: () => '%',
      },
      {
        title: 'settings.systemSettings.popup.revenueCalculation',
        key: 'affiliates_referral_payment_type',
        type: 'select',
        options: () => referralProgramDefaultSettingsOptions.value,
      },
    ],
    action: ({ affiliates_referral_enabled, affiliates_referral_percent, affiliates_referral_payment_type }: PutControlTowerCustomersAffiliateReferralProgramSettingsData['body']) =>
      api.putCustomersAffiliateReferralProgramSettings({
        affiliates_referral_enabled,
        affiliates_referral_percent: +affiliates_referral_percent,
        affiliates_referral_payment_type,
      }),
  },
  paymentsDefaultSettings: {
    title: 'settings.systemSettings.paymentsDefaultSettings',
    fields: [
      {
        title: 'settings.systemSettings.popup.paymentPeriod',
        key: 'affiliates_payments_net',
        type: 'select',
        options: () => paymentsDefaultSettingsOptions.value,
      },
      {
        title: 'settings.systemSettings.negativeCarryover',
        key: 'affiliates_payments_negative_carryover',
        label: (isEnabled: boolean) => (isEnabled ? 'settings.systemSettings.yes' : 'settings.systemSettings.no'),
        type: 'boolean',
      },
    ],
    action: (params: PutControlTowerCustomersAffiliatePaymentsSettingsData['body']) => api.putCustomersAffiliatePaymentsSettings(params),
  },
  domainReachability: {
    title: 'settings.systemSettings.productDomainReachability',
    fields: [
      {
        title: 'settings.systemSettings.checkingPath',
        key: 'domain_reachability_path',
        type: 'text',
        normalizeValue: (value) => {
          if (!value) return '';
          return `/${value.replace(/^\/+/, '')}`;
        },
      },
      {
        title: 'settings.systemSettings.sampleRate',
        key: 'domain_reachability_sample_rate',
        type: 'number',
        renderPost: () => '%',
      },
    ],
    action: ({ domain_reachability_path, domain_reachability_sample_rate }: PutControlTowerCustomersDomainReachabilitySettingsData['body']) =>
      api.putCustomersDomainReachabilitySettings({
        domain_reachability_path: domain_reachability_path || null,
        domain_reachability_sample_rate: +domain_reachability_sample_rate,
      }),
  },
  domainChangeNotifications: {
    title: 'settings.systemSettings.domainChangeNotifications',
    fields: [
      {
        title: 'settings.systemSettings.enableNotifications',
        key: 'service_domain_changes_notifications_enabled',
        label: (isEnabled: boolean) => (isEnabled ? 'settings.systemSettings.on' : 'settings.systemSettings.off'),
        type: 'boolean',
      },
    ],
    action: (params: PutControlTowerCustomersDefaultDomainsNotificationsSettingsData['body']) => api.putCustomersDefaultDomainsNotificationsSettings(params),
  },
  affiliateLimits: {
    title: 'settings.systemSettings.affiliateLimitsSettings',
    fields: [
      {
        title: 'settings.systemSettings.maxApiKeys',
        key: 'max_api_keys',
        type: 'number',
      },
      {
        title: 'settings.systemSettings.maxPromoCodes',
        key: 'max_promo_codes_levels',
        type: 'promo-codes-levels',
      },
      {
        title: 'settings.systemSettings.maxTrafficSources',
        key: 'max_traffic_sources',
        type: 'number',
      },
      {
        title: 'settings.systemSettings.maxPostbacksPerTrafficSource',
        key: 'max_postbacks_per_traffic_source',
        type: 'number',
      },
      {
        title: 'settings.systemSettings.maxTagsPerTrafficSource',
        key: 'max_tags_per_traffic_source',
        type: 'number',
      },
      {
        title: 'settings.systemSettings.maxBalanceToSetPaymentWallet',
        key: 'max_balance_to_set_payment_wallet',
        selectKey: 'max_balance_to_set_payment_wallet_currency_code',
        dataKeys: ['max_balance_to_set_payment_wallet_currency_code'],
        placeholder: 'settings.currencies.currency',
        type: 'input-select',
        options: () => customerCurrencies.value,
      },
      {
        title: 'settings.systemSettings.createPromoCodes',
        key: 'allow_promo_code_create_or_edit',
        label: (isEnabled: boolean) => (isEnabled ? 'permissions.allow' : 'permissions.deny'),
        type: 'boolean',
      },
    ],
    action: (params: PutControlTowerCustomersAffiliatesLimitsData['body']) =>
      api.putCustomersAffiliatesLimits({
        ...params,
        max_promo_codes_levels: params.max_promo_codes_levels?.length ? params.max_promo_codes_levels : null,
      }),
  },
} as Record<string, PopupConfig> ;
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
const filters = useFilters();
const { onSessionUnlock } = useSessionAction();
const popupManager = usePopupManager();
//#endregion

//#region data
const isLoading = ref(true);
const lazyLoading = ref(true);
const offersCatalog = ref<Offer[]>([]);

const data = ref<SystemSettingsData>({
  affiliate_program_name: '',
  transactional_email_from_mailbox: '',
  transactional_email_reply_to_address: '',
  transactional_email_from_domain: '',
  tos_url: '',
  privacy_policy_url: '',
  panel_contacts_enabled: false,
  panel_contact_ms_teams: '',
  panel_contact_telegram: '',
  panel_contact_email: '',
  panel_contact_us_form: '',
  app_id: '',
  app_key: '',
  app_secret_key: '',
  intercom_enabled: false,
  intercom_integration_status: 'unknown',
  intercom_integration_last_check: '',
  intercom_integration_error: '',
  kyc_integration_status: 'unknown',
  kyc_integration_last_check: '',
  kyc_integration_error: '',
  kyc_enabled: false,
  kyc_level_name: '',
  kyc_app_token: '',
  kyc_secret_key: '',
  kyc_secret_webhook_key: '',
  kyc_on_signup_mode: 'disabled',
  kyc_on_signup_country_list: [],
  kyc_on_first_payment_enabled: false,
  kyc_on_limits_enabled: false,
  kyc_on_limits_amount: null,
  kyc_on_limits_currency_code: '',
  kyc_on_demand_enabled: false,
  affiliates_referral_enabled: false,
  service_domain_changes_notifications_enabled: false,
  affiliates_referral_percent: 0,
  affiliates_referral_payment_type: 'ggr',
  affiliates_payments_net: 'net7',
  affiliates_payments_negative_carryover: false,
  domain_reachability_path: '',
  domain_reachability_sample_rate: 0,
  max_api_keys: 0,
  max_promo_codes_levels: [],
  max_traffic_sources: 0,
  max_postbacks_per_traffic_source: 0,
  max_tags_per_traffic_source: 0,
  allow_promo_code_create_or_edit: false,
  max_balance_to_set_payment_wallet: 0,
  max_balance_to_set_payment_wallet_currency_code: '',


  // сли что то сломается смотреть сюда
  offers_default_enabled: false,
  domain_distribution_enabled: false,
  affiliates_telegram_bot_token: null,
  affiliates_telegram_bot_username: null,
  // max_promo_codes: null,
  default_cataloged_offer_name: null,
  default_cataloged_offer_id: null,
  geo_traffic_enabled: false,
  geo_traffic_mode: 'allow_except',
  geo_traffic_countries: [],
  geo_traffic_allow_countries: [],
  geo_traffic_block_countries: [],
});
const fields = ref<FieldsConfig<SystemSettingsData>[]>([
  {
    header: {
      title: 'settings.systemSettings.affiliateProgramName',
      key: 'affiliateProgramName',
    },
    rows: [
      {
        renderValue: ({ affiliate_program_name }: SystemSettingsData) => `<span style="font-size: 24px; font-weight: 500">${affiliate_program_name}</span>`,
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.emailSettings',
      key: 'emailSettings',
    },
    rows: [
      {
        title: 'settings.systemSettings.transactionalEmailSender',
        renderValue: ({ transactional_email_from_mailbox, transactional_email_from_domain }: SystemSettingsData) => (transactional_email_from_mailbox ? `<span>${transactional_email_from_mailbox}@${transactional_email_from_domain}</span>` :
          t('user_info.not_specified')),
        hint: 'settings.systemSettings.serviceEmailHint',
      },
      {
        title: 'settings.systemSettings.replyTo',
        key: 'transactional_email_reply_to_address',
        hint: 'settings.systemSettings.supportEmailHint',
        defaultText: 'user_info.not_specified',
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.affiliatesTelegramBot',
      key: 'affiliatesTelegramBot',
      hint: 'settings.systemSettings.affiliatesTelegramBotHint',
    },
    rows: [
      {
        title: 'settings.systemSettings.botUsername',
        key: 'affiliates_telegram_bot_username',
        defaultText: 'user_info.not_specified',
      },
      {
        title: 'settings.systemSettings.botToken',
        key: 'affiliates_telegram_bot_token',
        defaultText: 'user_info.not_specified',
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.policies',
      key: 'policies',
      hint: 'settings.systemSettings.policiesHint',
    },
    rows: [
      {
        title: 'settings.systemSettings.termsOfServiceURL',
        renderValue: ({ tos_url }: SystemSettingsData) => (tos_url ? `<a href="${tos_url}" target="_blank" style="color: var(--text-link); text-decoration: none">${tos_url}</a>` : `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`),
      },
      {
        title: 'settings.systemSettings.privacyPolicyURL',
        renderValue: ({ privacy_policy_url }: SystemSettingsData) => (privacy_policy_url ? `<a href="${privacy_policy_url}" target="_blank" style="color: var(--text-link); text-decoration: none">${privacy_policy_url}</span>` : `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`),
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.intercomSetup',
      key: 'intercomSetup',
    },
    rows: [
      {
        title: 'settings.systemSettings.integrationStatus',
        renderStatus: ({
          intercom_integration_status,
          intercom_integration_last_check,
          intercom_integration_error,
        }: SystemSettingsData) => {
          return getStatus(
            intercom_integration_status,
            intercom_integration_last_check,
            intercom_integration_error,
          );
        },
      },
      {
        title: 'settings.systemSettings.appId',
        renderValue: ({ app_id }: SystemSettingsData) => {
          if (!app_id) {
            return `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`;
          }
          return `<span style="color: var(--text-link)">${t('user_info.specified')}</span>`;
        },
      },
      {
        title: 'settings.systemSettings.appKey',
        renderValue: ({ app_key }: SystemSettingsData) => {
          if (!app_key) {
            return `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`;
          }
          return `<span style="color: var(--text-link)">${t('user_info.specified')}</span>`;
        },
      },
      {
        title: 'settings.systemSettings.appSecretKey',
        renderValue: ({ app_secret_key }: SystemSettingsData) => {
          if (!app_secret_key) {
            return `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`;
          }
          return `<span style="color: var(--text-link)">${t('user_info.specified')}</span>`;
        },
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.kycSetup',
      key: 'kycSetup',
    },
    rows: [
      {
        title: 'settings.systemSettings.kycVerification',
        renderValue: ({ kyc_enabled }: SystemSettingsData) => `<span style="color: ${kyc_enabled ? 'var(--text-link)' : 'var(--text-negative)'}">
                ${kyc_enabled ? t('settings.systemSettings.enabled') : t('settings.systemSettings.disabled')}
              </span>`,
      },
      {
        title: 'settings.systemSettings.integrationStatus',
        renderStatus: ({
          kyc_integration_status,
          kyc_integration_last_check,
          kyc_integration_error,
        }: SystemSettingsData) => {
          return getStatus(
            kyc_integration_status,
            kyc_integration_last_check,
            kyc_integration_error,
          );
        },
      },
      {
        title: 'settings.systemSettings.sumsubLevelName',
        key: 'kyc_level_name',
        hint: 'settings.systemSettings.sumsubLevelNameHint',
        hintIsHtml: true,
        defaultText: 'user_info.not_specified',
      },
      {
        title: 'settings.systemSettings.sumsubAppToken',
        renderValue: ({ kyc_app_token }: SystemSettingsData) => {
          if (!kyc_app_token) {
            return `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`;
          }
          return `<span style="color: var(--text-link)">${t('user_info.specified')}</span>`;
        },
      },
      {
        title: 'settings.systemSettings.sumsubSecretKey',
        renderValue: ({ kyc_secret_key }: SystemSettingsData) => {
          if (!kyc_secret_key) {
            return `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`;
          }
          return `<span style="color: var(--text-link)">${t('user_info.specified')}</span>`;
        },
      },
      {
        title: 'settings.systemSettings.sumsubSecretWebhookKey',
        hint: 'settings.systemSettings.sumsubWebhookHint',
        hintIsHtml: true,
        renderValue: ({ kyc_secret_webhook_key }: SystemSettingsData) => {
          if (!kyc_secret_webhook_key) {
            return `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`;
          }
          return `<span style="color: var(--text-link)">${t('user_info.specified')}</span>`;
        },
      },
      {
        title: 'settings.systemSettings.kycAfterSignUp',
        renderValue: ({ kyc_on_signup_mode, kyc_enabled }: SystemSettingsData) => {
          if (!kyc_enabled) {
            return `<span style="color: var(--text-body-tertiary)">${t('settings.systemSettings.disabled')}</span>`;
          }

          if (kyc_on_signup_mode === 'everyone') {
            return t('settings.systemSettings.popup.kyc_after_sign_up_required_for_everyone');
          }
          if (kyc_on_signup_mode === 'selected_countries') {
            return t('settings.systemSettings.popup.kyc_after_sign_up_required_for_selected_countries');
          }

          return `<span style="color: var(--text-negative)">${t('settings.systemSettings.disabled')}</span>`;
        },
      },
      {
        title: 'settings.systemSettings.kycDependingOnPayments',
        renderValue: ({ kyc_on_first_payment_enabled, kyc_enabled }: SystemSettingsData) => {
          if (!kyc_enabled) {
            return `<span style="color: var(--text-body-tertiary)">${t('settings.systemSettings.disabled')}</span>`;
          }

          return kyc_on_first_payment_enabled
            ? `<span>${t('settings.systemSettings.popup.kyc_depending_on_payments_required_before_first_payment')}</span>`
            : `<span style="color: var(--text-negative)">${t('settings.systemSettings.disabled')}</span>`;
        },
      },
      {
        title: 'settings.systemSettings.kycDependingOnLimits',
        renderValue: ({
          kyc_on_limits_enabled,
          kyc_on_limits_amount,
          kyc_on_limits_currency_code,
          kyc_enabled,
        }: SystemSettingsData) => {
          if (!kyc_enabled) {
            return `<span style="color: var(--text-body-tertiary)">${t('settings.systemSettings.disabled')}</span>`;
          }

          if (!kyc_on_limits_enabled) {
            return `<span style="color: var(--text-negative)">${t('settings.systemSettings.disabled')}</span>`;
          }

          const label = t('settings.systemSettings.popup.kyc_depending_on_limits_required_for_amount_above');
          const amount = kyc_on_limits_amount && kyc_on_limits_currency_code && `${getCurrencyLabel(kyc_on_limits_currency_code)}${kyc_on_limits_amount < 0 ? '-' : ''}${numeral(Math.abs(kyc_on_limits_amount)).format('0,0.[0000]')}`;

          return `<span>${label}${amount ? ` ${amount}` : ''}</span>`;
        },
      },
      {
        title: 'settings.systemSettings.kycOnDemand',
        renderValue: ({ kyc_on_demand_enabled, kyc_enabled }: SystemSettingsData) => {
          if (!kyc_enabled) {
            return `<span style="color: var(--text-body-tertiary)">${t('settings.systemSettings.disabled')}</span>`;
          }

          return `<span style="color: ${kyc_on_demand_enabled ? 'var(--text-link)' : 'var(--text-negative)'}">
                  ${kyc_on_demand_enabled ? t('settings.systemSettings.enabled') : t('settings.systemSettings.disabled')}
                </span>`;
        },
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.supportContacts',
      key: 'supportContacts',
      hint: 'settings.systemSettings.supportContactsHint',
    },
    rows: [
      {
        title: 'settings.systemSettings.showContactsInAffiliatePanel',
        renderValue: ({ panel_contacts_enabled }: SystemSettingsData) => `<span style="color: ${panel_contacts_enabled ? 'var(--text-link)' : 'var(--text-negative)'}">
            ${panel_contacts_enabled ? t('settings.systemSettings.enabled') : t('settings.systemSettings.disabled')}
          </span>`,
      },
      {
        title: 'settings.systemSettings.ms_teams',
        messenger: 'ms_teams',
        key: 'panel_contact_ms_teams',
        defaultText: 'user_info.not_specified',
      },
      {
        title: 'settings.systemSettings.telegram',
        messenger: 'telegram',
        key: 'panel_contact_telegram',
        defaultText: 'user_info.not_specified',
      },
      {
        title: 'settings.systemSettings.email',
        preIcon: 'envelope',
        key: 'panel_contact_email',
        defaultText: 'user_info.not_specified',
      },
      {
        title: 'settings.systemSettings.contact_us_form',
        key: 'panel_contact_us_form',
        defaultText: 'user_info.not_specified',
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.geoblockSettings',
      key: 'geoblockSettings',
    },
    rows: [
      {
        title: 'settings.systemSettings.geoblock',
        renderValue: ({ geo_traffic_enabled }: SystemSettingsData) => {
          return `<span style="color: ${geo_traffic_enabled ? 'var(--text-link)' : 'var(--text-negative)'}">
              ${geo_traffic_enabled ? t('settings.systemSettings.enabled') : t('settings.systemSettings.disabled')}
            </span>`;
        },
      },
      {
        title: '',
        renderValue: ({ geo_traffic_mode, geo_traffic_countries }: SystemSettingsData) => {
          const modeLabel = geo_traffic_mode === 'decline_except'
            ? `<span style="color: var(--text-negative)">${t('settings.systemSettings.blockAllExcept')}:</span>`
            : `<span style="color: var(--text-link)">${t('settings.systemSettings.allowAllExcept')}:</span>`;

          if (!geo_traffic_countries?.length) {
            return `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`;
          }

          const countryNames = geo_traffic_countries
          .map((countryCode) => countriesNameMap.value[countryCode] || countryCode)
          .join(', ');

          return `${modeLabel} ${countryNames}`;
        },
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.default_offer',
      key: 'defaultOffer',
    },
    rows: [
      {
        title: 'settings.systemSettings.default_offer',
        renderValue: ({ default_cataloged_offer_name }: SystemSettingsData) => (default_cataloged_offer_name ? `${default_cataloged_offer_name}` : `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`),
      },
      {
        title: 'settings.systemSettings.domain_offer',
        renderValue: ({ cataloged_offer_name }: SystemSettingsData) => (cataloged_offer_name ? `${cataloged_offer_name}` : `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`),
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.referralProgramDefaultSettings',
      key: 'referralProgramDefaultSettings',
    },
    rows: [
      {
        title: 'settings.systemSettings.referralProgram',
        renderValue: ({ affiliates_referral_enabled }: SystemSettingsData) => `<span style="color: ${affiliates_referral_enabled ? 'var(--text-link)' : 'var(--text-negative)'}">
                ${affiliates_referral_enabled ? t('settings.systemSettings.enabled') : t('settings.systemSettings.disabled')}
              </span>`,
      },
      {
        title: 'settings.systemSettings.referralPercent',
        renderValue: ({ affiliates_referral_percent, affiliates_referral_payment_type }: SystemSettingsData) => `${affiliates_referral_percent}% ${getReferralPaymentType(affiliates_referral_payment_type)}`,
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.paymentsDefaultSettings',
      key: 'paymentsDefaultSettings',
    },
    rows: [
      {
        title: 'settings.systemSettings.period',
        renderValue: ({ affiliates_payments_net }: SystemSettingsData) => affiliates_payments_net && `<span>${t(`settings.systemSettings.popup.${affiliates_payments_net}`)}</span>`,
      },
      {
        title: 'settings.systemSettings.negativeCarryover',
        renderValue: ({ affiliates_payments_negative_carryover }: SystemSettingsData) => `<span style="color: ${affiliates_payments_negative_carryover ? 'var(--text-link)' : 'var(--text-negative)'}">
                ${affiliates_payments_negative_carryover ? t('settings.systemSettings.yes') : t('settings.systemSettings.no')}
              </span>`,
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.productDomainReachability',
      key: 'domainReachability',
    },
    rows: [
      {
        title: 'settings.systemSettings.checkingPath',
        key: 'domain_reachability_path',
      },
      {
        title: 'settings.systemSettings.sampleRate',
        renderValue: ({ domain_reachability_sample_rate }: SystemSettingsData) => `${domain_reachability_sample_rate}%`,
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.domainChangeNotifications',
      key: 'domainChangeNotifications',
    },
    rows: [
      {
        title: 'settings.systemSettings.enableNotifications',
        renderValue: ({ service_domain_changes_notifications_enabled }: SystemSettingsData) => `<span style="color: ${service_domain_changes_notifications_enabled ? 'var(--text-link)' : 'var(--text-negative)'}">
                ${service_domain_changes_notifications_enabled ? t('settings.systemSettings.on') : t('settings.systemSettings.off')}
              </span>`,
      },
    ],
  },
  {
    header: {
      title: 'settings.systemSettings.affiliateLimits',
      key: 'affiliateLimits',
    },
    rows: [
      {
        title: 'settings.systemSettings.maxApiKeys',
        key: 'max_api_keys',
        defaultText: 'user_info.not_specified',
      },
      {
        title: 'settings.systemSettings.maxPromoCodes',
        renderValue: ({ max_promo_codes_levels }: SystemSettingsData) => {
          if (!max_promo_codes_levels?.length) {
            return `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`;
          }

          return '';
        },
      },
      {
        className: 'system-settings__max-promo-levels-row',
        show: ({ max_promo_codes_levels }: SystemSettingsData) => !!max_promo_codes_levels?.length,
        renderValue: ({ max_promo_codes_levels }: SystemSettingsData) => {
          if (max_promo_codes_levels) {
            return max_promo_codes_levels
              .map((level) => `
                    <div class="system-settings__max-promo-levels-row-item">
                      <div class="t:flex t:items-center">
                        <i class="fa fas fa-greater-than-equal less-or-equal"></i>
                        <span class="t:ml-4">${level.ftd_count || 0} FTD</span>
                      </div>
                      <span>${level.max_amount || 0}</span>
                    </div>
                  `)
              .join('');
          }
          return '';
        },
      },
      {
        title: 'settings.systemSettings.maxTrafficSources',
        key: 'max_traffic_sources',
        defaultText: 'user_info.not_specified',
      },
      {
        title: 'settings.systemSettings.maxPostbacksPerTrafficSource',
        key: 'max_postbacks_per_traffic_source',
        defaultText: 'user_info.not_specified',
      },
      {
        title: 'settings.systemSettings.maxTagsPerTrafficSource',
        key: 'max_tags_per_traffic_source',
        defaultText: 'user_info.not_specified',
      },
      {
        title: 'settings.systemSettings.maxBalanceToSetPaymentWallet',
        defaultText: 'user_info.not_specified',
        renderValue: ({
          max_balance_to_set_payment_wallet,
          max_balance_to_set_payment_wallet_currency_code,
        }: SystemSettingsData) => {
          if (max_balance_to_set_payment_wallet && max_balance_to_set_payment_wallet_currency_code) {
            return `${getCurrencyLabel(max_balance_to_set_payment_wallet_currency_code)}${max_balance_to_set_payment_wallet < 0 ? '-' : ''}${numeral(Math.abs(max_balance_to_set_payment_wallet)).format('0,0')}`;
          }

          return `<span style="color: var(--text-body-tertiary)">${t('user_info.not_specified')}</span>`;
        },
      },
      {
        title: 'settings.systemSettings.createPromoCodes',
        renderValue: ({ allow_promo_code_create_or_edit }: SystemSettingsData) => `<span style="color: ${allow_promo_code_create_or_edit ? 'var(--text-link)' : 'var(--text-negative)'}">
                ${allow_promo_code_create_or_edit ? t('permissions.allow') : t('permissions.deny')}
              </span>`,
      },
    ],
  },
]);
//#endregion

//#region computed
const currencies = computed(() => store.getters['misc/currencies']);
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
const countries = computed(() => store.getters['misc/countries']);
const countriesNameMap = computed(() => store.getters['misc/countriesNameMap']);

const kycAfterSignUpOptions = computed(() => {
  return [
    { value: 'disabled', label: t('settings.systemSettings.disabled') },
    { value: 'everyone', label: t('settings.systemSettings.popup.kyc_after_sign_up_required_for_everyone') },
    { value: 'selected_countries', label: t('settings.systemSettings.popup.kyc_after_sign_up_required_for_selected_countries') },
  ];
});

const kycDependingOnPaymentsOptions = computed(() => {
  return [
    { value: false, label: t('settings.systemSettings.disabled') },
    { value: true, label: t('settings.systemSettings.popup.kyc_depending_on_payments_required_before_first_payment') },
  ];
});

const kycDependingOnLimitsOptions = computed(() => {
  return [
    { value: false, label: t('settings.systemSettings.disabled') },
    { value: true, label: t('settings.systemSettings.popup.kyc_depending_on_limits_required_for_amount_above') },
  ];
});

const kycOnDemandOptions = computed(() => {
  return [
    { value: false, label: t('settings.systemSettings.disabled') },
    { value: true, label: t('settings.systemSettings.enabled') },
  ];
});

const paymentsDefaultSettingsOptions = computed(() => {
  return [
    {
      code: 'net7',
      name: t('settings.systemSettings.popup.net7'),
    },
    {
      code: 'net15',
      name: t('settings.systemSettings.popup.net15'),
    },
    {
      code: 'net30',
      name: t('settings.systemSettings.popup.net30'),
    },
    {
      code: 'net60',
      name: t('settings.systemSettings.popup.net60'),
    },
    {
      code: 'manual',
      name: t('settings.systemSettings.popup.manual'),
    },
  ];
});

const referralProgramDefaultSettingsOptions = computed(() => {
  return [
    {
      code: 'ggr',
      name: t('settings.systemSettings.popup.ggr'),
    },
    {
      code: 'ngr',
      name: t('settings.systemSettings.popup.ngr'),
    },
    {
      code: 'net_project_profit',
      name: t('settings.systemSettings.popup.net_project_profit'),
    },
  ];
});

const offersCatalogGrouped = computed(() => {
  return Object.values(offersCatalog.value.reduce((acc, offer) => {
    const categoryId = offer.category_id;
    const categoryName = offer.category_name;

    if (!acc[categoryId]) {
      acc[categoryId] = {
        category_id: categoryId,
        category_name: categoryName,
        offers: [],
      };
    }
    acc[categoryId].offers.push(offer);
    return acc;
  }, {} as Record<string, GroupedCategory>));
});

const fieldsColumns = computed(() => {
  const referralIndex = fields.value.findIndex((field) => field.header.key === 'supportContacts');
  return {
    left: fields.value.slice(0, referralIndex),
    right: fields.value.slice(referralIndex),
  };
});
//#endregion

//#region methods
const handleEditBlock = (id: keyof typeof popup) => {
  popupManager.systemSettings.open({
    data: {
      popupData: popup[id]!.fields.reduce((acc, { key, dataKeys }) => {
        acc[key] = data.value[key];

        if (dataKeys) {
          dataKeys.forEach((dataKey) => {
            acc[dataKey] = data.value[dataKey as keyof SystemSettingsData];
          });
        }

        return acc;
      }, {} as Record<string, any> ),
      popupTitle: popup[id]?.title || '',
      popupInfo: popup[id]?.info || '',
      popupFields: popup[id]?.fields || [],
      popupAction: popup[id]?.action ?? (() => Promise.resolve()),
    },
    listeners: {
      success: () => {
        fetchData();
      },
    },
  });
};

const getCurrencyLabel = (code: string) => {
  const currency = currencies.value.find((el) => el.code === code);

  if (currency) {
    return currency.symbol || `${currency.code} `;
  }

  return '';
};

const getReferralPaymentType = (key: string) => {
  if (!key) return '';

  return t(`settings.systemSettings.popup.${key}`);
};

const getStatus = (status: string = 'unknown', lastCheck: string, error: string) => {
  const statusMap = {
    disabled: {
      label: t('settings.systemSettings.statuses.disabled'),
      color: 'var(--text-negative)',
    },
    on: {
      label: t('settings.systemSettings.statuses.on'),
      color: 'var(--text-link)',
    },
    error: {
      label: t('settings.systemSettings.statuses.error'),
      color: 'var(--text-negative)',
    },
    unknown: {
      label: t('settings.systemSettings.statuses.unknown'),
      color: 'var(--text-body-tertiary)',
    },
  };
  const currentStatus = statusMap[status as keyof typeof statusMap] || statusMap.unknown;

  const hintLines = [];
  if (lastCheck) {
    hintLines.push(`${t('settings.systemSettings.lastCheck')}: ${filters.formatDate3(lastCheck)}`);
  }
  if (error) {
    hintLines.push(`${t('settings.systemSettings.note')}: ${error}`);
  }

  return {
    text: currentStatus.label,
    color: currentStatus.color,
    hint: hintLines.length ? hintLines.join('\n') : null,
  };
};

const normalizeIntegrationStatus = (value: string) => {
  switch (value.toLowerCase()) {
    case 'ok':
      return 'on';
    case 'error':
      return 'error';
    case 'disabled':
      return 'disabled';
    case 'unknown':
    default:
      return 'unknown';
  }
};

const getIntegrationState = (rawData: IntegrationStatuses) => {
  return {
    status: normalizeIntegrationStatus(rawData.status || 'unknown'),
    lastCheck: rawData.last_checked_at || '',
    error: rawData.error_message || '',
  };
};

const normalizeKycRules = (value: unknown): KycRules => {
  const toRecordOrNull = (input: unknown): Record<string, unknown> | null => (
    input !== null && typeof input === 'object' ? input as Record<string, unknown> : null
  );
  const rules = toRecordOrNull(value);
  const onSignup = toRecordOrNull(rules?.on_signup);
  const onDemand = toRecordOrNull(rules?.on_demand);
  const onFirstPayment = toRecordOrNull(rules?.on_first_payment);
  const onLimits = toRecordOrNull(rules?.on_limits);

  return {
    on_signup: {
      enabled: !!onSignup?.enabled,
      country_list: Array.isArray(onSignup?.country_list)
        ? onSignup.country_list
        : [],
    },
    on_demand: {
      enabled: !!onDemand?.enabled,
    },
    on_first_payment: {
      enabled: !!onFirstPayment?.enabled,
    },
    on_limits: {
      enabled: !!onLimits?.enabled,
      amount: onLimits?.amount ? +onLimits.amount : null,
      currency_code: typeof onLimits?.currency_code === 'string' ? onLimits.currency_code : null,
    },
  };
};

const getOffersCatalog = () => {
  return api.getAffiliateOffersCatalog().then((res) => {
    offersCatalog.value = res.data.payload;
  });
};

const fetchData = async () => {
  isLoading.value = true;

  getOffersCatalog();

  return Promise.all([
    api.getCustomersAffiliateProgramName().then((res) => res.data.payload),
    api.getCustomersEmailSettings().then((res) => res.data.payload),
    api.getCustomersAffiliateTelegramBotSettings().then((res) => res.data.payload),
    api.getCustomersPrivacyPolicySettings().then((res) => res.data.payload),
    api.getCustomersSupportContactsSettings().then((res) => res.data.payload),
    api.getCustomersIntercomSettings().then((res) => res.data.payload),
    api.getCustomersKycSettings().then((res) => res.data.payload),
    api.getCustomersAffiliateReferralProgramSettings().then((res) => res.data.payload),
    api.getCustomersAffiliatePaymentsSettings().then((res) => res.data.payload),
    api.getCustomersDefaultDomainsNotificationsSettings().then((res) => res.data.payload),
    api.getCustomersAffiliatesLimits().then((res) => res.data.payload),
    api.getCustomersOfferDistributionSettings().then((res) => res.data.payload),
    api.getCustomersGeoTrafficSettings().then((res) => res.data.payload),
    api.getCustomersDomainReachabilitySettings().then((res) => res.data.payload),
    api.getCustomersIntegrationStatuses().then((res) => ({
      intercom_status: res.data.payload.intercom,
      sumsub_status: res.data.payload.sumsub,
    })),
  ]).then((res) => {
    const {
      distribute_by_email_domain,
      kyc_rules,
      kyc_enabled,
      kyc_app_token,
      kyc_secret_key,
      kyc_secret_webhook_key,
      kyc_level_name,
      default_cataloged_offer_id,
      geo_traffic_mode,
      geo_traffic_countries,
      intercom_status,
      sumsub_status,
      ...restData
    } = res.reduce((acc, item) => ({
      ...acc,
      ...item,
    }), {}) as GetControlTowerCustomersAffiliateProgramNameResponse['payload']
      & GetControlTowerCustomersEmailSettingsResponse['payload']
      & GetControlTowerCustomersAffiliateTelegramBotSettingsResponse['payload']
      & GetControlTowerCustomersPrivacyPolicySettingsResponse['payload']
      & GetControlTowerCustomersSupportContactsSettingsResponse['payload']
      & GetControlTowerCustomersIntercomSettingsResponse['payload']
      & GetControlTowerCustomersKycSettingsResponse['payload']
      & GetControlTowerCustomersAffiliateReferralProgramSettingsResponse['payload']
      & GetControlTowerCustomersAffiliatePaymentsSettingsResponse['payload']
      & GetControlTowerCustomersDefaultDomainsNotificationsDefaultSettingsResponse['payload']
      & GetControlTowerCustomersAffiliatesLimitsResponse['payload']
      & GetControlTowerCustomersOfferDistributionSettingsResponse['payload']
      & GetControlTowerCustomersGeoTrafficSettingsResponse['payload']
      & GetControlTowerCustomersDomainReachabilitySettingsResponse['payload']
      & IntegrationStatusesPayload;

    const normalizedKycRules = normalizeKycRules(kyc_rules);
    const onSignup = normalizedKycRules.on_signup;
    const onSignupMode = !onSignup.enabled
      ? 'disabled'
      : (onSignup.country_list?.length ? 'selected_countries' : 'everyone');
    const intercomIntegration = getIntegrationState(intercom_status);
    const sumsubIntegration = getIntegrationState(sumsub_status);

    const result = {
      ...restData,
      intercom_enabled: intercomIntegration.status !== 'disabled',
      app_id: restData.app_id || '',
      app_key: restData.app_key || '',
      app_secret_key: restData.app_secret_key || '',
      intercom_integration_status: intercomIntegration.status,
      intercom_integration_last_check: intercomIntegration.lastCheck,
      intercom_integration_error: intercomIntegration.error,
      kyc_integration_status: sumsubIntegration.status,
      kyc_integration_last_check: sumsubIntegration.lastCheck,
      kyc_integration_error: sumsubIntegration.error,
      kyc_enabled: !!kyc_enabled,
      kyc_level_name: kyc_level_name || '',
      kyc_app_token: kyc_app_token || '',
      kyc_secret_key: kyc_secret_key || '',
      kyc_secret_webhook_key: kyc_secret_webhook_key || '',
      kyc_on_signup_mode: onSignupMode,
      kyc_on_signup_country_list: onSignup.country_list || [],
      kyc_on_demand_enabled: normalizedKycRules.on_demand.enabled,
      kyc_on_first_payment_enabled: normalizedKycRules.on_first_payment.enabled,
      kyc_on_limits_enabled: normalizedKycRules.on_limits.enabled,
      kyc_on_limits_amount: normalizedKycRules.on_limits.amount || null,
      kyc_on_limits_currency_code: normalizedKycRules.on_limits.currency_code || '',
      default_cataloged_offer_id: default_cataloged_offer_id,
      offers_default_enabled: !!default_cataloged_offer_id,
      domain_distribution_enabled: !!distribute_by_email_domain,
      geo_traffic_enabled: !!geo_traffic_countries?.length,
      geo_traffic_mode: geo_traffic_mode || 'allow_except',
      geo_traffic_countries: geo_traffic_countries || [],
      geo_traffic_allow_countries: geo_traffic_mode === 'allow_except' ? (geo_traffic_countries || []) : [],
      geo_traffic_block_countries: geo_traffic_mode === 'decline_except' ? (geo_traffic_countries || []) : [],
      ...(distribute_by_email_domain && {
        cataloged_offer_id: distribute_by_email_domain.cataloged_offer_id,
        cataloged_offer_name: distribute_by_email_domain.cataloged_offer_name,
        email_domains: distribute_by_email_domain.email_domains.join('\n'),
      }),
    };
    data.value = result;
    return result;
  }).finally(() => isLoading.value = false);
};
//#endregion

//#region hooks
onMounted(() => {
  fetchData();
  nextTick(() => {
    setTimeout(() => {
      lazyLoading.value = false;
    }, 200);
  });
});

onSessionUnlock.value = fetchData;
fetchData();
//#endregion
</script>

<template>
  <div class="system-settings">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('settings.systemSettings.title') }}
      </template>
    </UiToolbar>
    <div
      v-loading="isLoading"
      :class="[
        't:grid t:grid-cols-2 t:gap-32',
        { 'system-settings__lazy-loading': lazyLoading }
      ]"
    >
      <div class="system-settings__column t:space-y-16">
        <CardBlock
          v-for="field in fieldsColumns.left"
          :key="field.header.key"
          :title="$t(field.header.title)"
          :tooltip="field.header.hint ? $t(field.header.hint) : ''"
        >
          <template v-slot:action>
            <UiAction
              :action-name="$t(`affiliates.affiliatesInfo.edit`)"
              @click="handleEditBlock(field.header.key)"
            />
          </template>
          <template v-slot:cardItem>
            <template
              v-for="(row, index) in field.rows"
              :key="index"
            >
              <CardItem
                v-if="!row.show || row.show(data)"
                :item="{
                  name: row.title ? $t(row.title) : null,
                  value: row.key ? data[row.key] : '',
                }"
                :no-border="index === 0"
                :is-html="!!row.renderValue"
                :class="row.className"
                :pre-icon="row.preIcon"
                :hint="row.hint ? $t(row.hint) : null"
                :hint-is-html="!!row.hintIsHtml"
              >
                <template v-slot:value>
                  <div
                    v-if="!!row.renderValue"
                    class="system-settings__render-value t:truncate"
                    v-html="row.renderValue(data)"
                  />
                  <div
                    v-else-if="!!row.renderStatus"
                    class="t:flex t:items-center t:gap-4"
                  >
                    <span
                      class="system-settings__render-value t:truncate"
                      :style="{ color: row.renderStatus(data).color }"
                    >
                      {{ row.renderStatus(data).text }}
                    </span>
                    <UiTooltip
                      v-if="row.renderStatus(data).hint"
                      :tooltip="row.renderStatus(data).hint"
                      popover-class="t:whitespace-pre-line"
                    >
                      <UiIcon
                        name="question-circle"
                        class="t:cursor-pointer"
                        :size="14"
                        :color="row.renderStatus(data).color"
                      />
                    </UiTooltip>
                  </div>
                </template>
                <template
                  v-if="row.messenger"
                  v-slot:title
                >
                  <UiMessenger
                    :messenger-type="row.messenger"
                    :messenger-address="$t(row.title || '')"
                  />
                </template>
              </CardItem>
            </template>
          </template>
        </CardBlock>
      </div>
      <div class="system-settings__column t:space-y-16">
        <CardBlock
          v-for="field in fieldsColumns.right"
          :key="field.header.key"
          :title="$t(field.header.title)"
          :tooltip="field.header.hint ? $t(field.header.hint) : ''"
        >
          <template v-slot:action>
            <UiAction
              :action-name="$t(`affiliates.affiliatesInfo.edit`)"
              @click="handleEditBlock(field.header.key)"
            />
          </template>
          <template v-slot:cardItem>
            <template
              v-for="(row, index) in field.rows"
              :key="index"
            >
              <CardItem
                v-if="!row.show || row.show(data)"
                :item="{
                  name: row.title ? $t(row.title) : null,
                  value: row.key ? data[row.key] : '',
                }"
                :no-border="index === 0"
                :is-html="!!row.renderValue"
                :class="row.className"
                :pre-icon="row.preIcon"
                :hint="row.hint ? $t(row.hint) : null"
                :hint-is-html="!!row.hintIsHtml"
              >
                <template v-slot:value>
                  <div
                    v-if="!!row.renderValue"
                    class="system-settings__render-value t:truncate"
                    v-html="row.renderValue(data)"
                  />
                  <div
                    v-else-if="!!row.renderStatus"
                    class="t:flex t:items-center t:gap-4"
                  >
                    <span
                      class="system-settings__render-value t:truncate"
                      :style="{ color: row.renderStatus(data).color }"
                    >
                      {{ row.renderStatus(data).text }}
                    </span>
                    <UiTooltip
                      v-if="row.renderStatus(data).hint"
                      :tooltip="row.renderStatus(data).hint"
                      popover-class="t:whitespace-pre-line"
                    >
                      <UiIcon
                        name="question-circle"
                        class="t:cursor-pointer"
                        :size="14"
                        :color="row.renderStatus(data).color"
                        substyle="fa-solid"
                      />
                    </UiTooltip>
                  </div>
                </template>
                <template
                  v-if="row.messenger"
                  v-slot:title
                >
                  <UiMessenger
                    :messenger-type="row.messenger"
                    :messenger-address="$t(row.title || '')"
                  />
                </template>
              </CardItem>
            </template>
          </template>
        </CardBlock>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.system-settings{
  &__lazy-loading {
    display: none;
  }
  &__max-promo-levels-row.card-item {
    margin-left: 8px;
    padding: 0;
    .el-tooltip__trigger{
      width: 100%;
    }
  }
  &__max-promo-levels-row-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    font-size: 14px;
    i {
      font-size: 12px;
    }
    & + .system-settings__max-promo-levels-row-item {
      border-top: 1px solid var(--border-divider);
    }
  }
}
</style>
